import { create } from 'zustand'
import { api, type User, type Profile, type DeploymentKind } from '@/lib/api/client'
import { profileService } from '@/lib/api/profileService'

interface AuthState {
  user: User | null
  profile: Profile | null
  isAuthenticated: boolean
  isLoading: boolean
  isInitialized: boolean
  error: string | null
  /** Тип развёртывания сервера ('standalone'|'managed') — runtime-детекция. */
  deployment: DeploymentKind | null
  /** OIDC-вход включён на сервере (GET /auth/status.oidc_enabled). Нужен логин-форме
   *  для выбора ветки полей (username+password vs apiKey) без «мигающей» промежуточной формы. */
  oidcEnabled: boolean
  /** Debug-режим сервера (GET /auth/status.debug_mode) — список тестовых пользователей. */
  debugMode: boolean

  initialize: () => Promise<void>
  reauthenticate: () => Promise<void>
  login: (apiKey: string, username?: string) => Promise<void>
  loginWithPassword: (server: string, username: string, password: string) => Promise<void>
  setUser: (user: User) => Promise<void>
  /** Локальное обновление профиля (после PATCH /profile) без повторной загрузки. */
  setProfile: (profile: Profile | null) => void
  logout: () => Promise<void>
  sessionExpired: () => void
  clearError: () => void
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  profile: null,
  isAuthenticated: false,
  isLoading: false,
  isInitialized: false,
  error: null,
  deployment: null,
  oidcEnabled: false,
  debugMode: false,

  initialize: async () => {
    if (get().isLoading || get().isInitialized) return
    await get().reauthenticate()
  },

  /**
   * Принудительно перечитать сессию (без guard) — когда cookie уже поставлена
   * (например после server-логина), а состояние UI нужно обновить.
   *
   * Использует только GET /auth/status (200 всегда, в т.ч. для гостя), чтобы не
   * провоцировать ожидаемый, но шумный 401 от GET /auth/session в консоли.
   */
  reauthenticate: async () => {
    set({ isLoading: true, error: null })
    try {
      const status = await api.getStatus()
      set({
        user: status.authenticated ? status.user : null,
        isAuthenticated: status.authenticated,
        isLoading: false,
        deployment: status.deployment ?? null,
        oidcEnabled: status.oidc_enabled,
        debugMode: status.debug_mode,
      })
      if (status.authenticated) {
        await profileService.initialize()
      }
      set({ isInitialized: true })
    } catch {
      // Сеть/сервер недоступны — безопасно завершаем инициализацию гостем.
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        isInitialized: true,
        deployment: null,
        oidcEnabled: false,
        debugMode: false,
      })
    }
  },

  login: async (apiKey: string, username?: string) => {
    set({ isLoading: true, error: null })
    try {
      const response = await api.spaLogin({ api_key: apiKey, username })
      set({
        user: response.user,
        isAuthenticated: true,
        isLoading: false,
        deployment: response.deployment ?? null,
      })
      await profileService.initialize()
      set({ isInitialized: true })
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : 'Login failed',
        isLoading: false,
        isInitialized: true,
      })
    }
  },

  loginWithPassword: async (server: string, username: string, password: string) => {
    set({ isLoading: true, error: null })
    try {
      const response = await api.loginWithPassword(server, username, password)
      // Обычный runtime-вход: личность приходит с сервера сразу (без перезапуска
      // процесса/страницы), cookie ставит core.
      set({
        user: response.user ?? null,
        isAuthenticated: true,
        isLoading: false,
        deployment: response.deployment ?? null,
      })
      await profileService.initialize()
      set({ isInitialized: true })
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : 'Login failed',
        isLoading: false,
        isInitialized: true,
      })
    }
  },

  /**
   * Установить пользователя после server-логина: cookie уже поставлена core,
   * здесь — только состояние UI (+ профиль).
   */
  setUser: async (user: User) => {
    set({ user, isAuthenticated: true, isLoading: false, error: null })
    await profileService.initialize()
    set({ isInitialized: true })
  },

  setProfile: (profile) => set({ profile }),

  logout: async () => {
    set({ isLoading: true })
    try {
      // Серверная часть: logoutRemote (revoke refresh-токена + удаление credentials
      // активного сервера + runtime disconnect) + очистка cookie. Профиль сервера
      // из реестра не удаляется — сервер остаётся в knownServers/lastServer.
      await api.logout()
    } finally {
      localStorage.removeItem('synth-ui.i18n.v1')
      localStorage.removeItem('synth-ui-settings')
      // Тема — браузерная настройка, а не часть личности пользователя: она
      // переживает выход, чтобы экран входа был в последней использованной
      // теме (cookie + localStorage, см. lib/theme/theme-persistence).
      // deployment/oidcEnabled/debugMode описывают сервер, а не сессию, и после
      // выхода нужны LoginForm для корректной ветки полей. Сбрасывать их нельзя:
      // reauthenticate() здесь не вызывается (isInitialized остаётся true), и при
      // сбросе логин-форма снова показала бы промежуточную password-only форму
      // (managed OIDC) либо спрятала поле «Сервер» (гибрид).
      set({ user: null, profile: null, isAuthenticated: false, isLoading: false })
    }
  },

  sessionExpired: () =>
    set({ user: null, profile: null, isAuthenticated: false, isLoading: false }),

  clearError: () => set({ error: null }),
}))
